import React, { useEffect } from 'react';
import { InputField } from '@trp-beacon-ui-kit/react-controls';
import { useAuthState } from '@trp-ta-nitro/auth-react';
import config from '../../config';
import PhoneNumber from '../../components/PhoneNumber/PhoneNumber';
import ConfirmationHeading from '../../components/ConfirmationSection/ConfirmationHeading';
import {
  accountTypes,
  chargeTypes,
  isTrust,
  isTrustExistingFlow,
  isNonFundedFlow,
  isRetirement,
  isTaxableTransfer
} from '../../flows/shared/utils.ts';
import ConfirmationSection from '../../components/ConfirmationSection/ConfirmationSection';
import { MFBeaconButton } from '../../components/Beacon/BeaconComponents';
import { emailValidation } from '../../flows/shared/validations';
import styles from './styles.css';
import appStoreIcon from '../../assets/images/app-store-icon.svg';
import playStoreIcon from '../../assets/images/play-store-icon.svg';
import ExternalAuthenticator from '../../components/BaseLayout/ExternalAuthenticator';
import { generateGeneralTestID } from '../../generateAutomationTestIDs.ts';
import NonFundedConfirmation from '../../components/ConfirmationSection/NonFundedConfirmation.js';
import RetirementContactSection from '../../components/ConfirmationSection/RetirementContactSection.js';

const LoginInfoSection = ({ accountType, rpsDirectRollover = false }) => {
  // fetch users entitlements to determine whether they are retail enabled
  const authState = useAuthState();
  const isGuestTypeUser = !authState?.entitlements?.includes(
    'TRP_RET_EXTERNAL_USER_ACCESS'
  );

  return (
    <>
      {isRetirement(accountType) ? (
        <>
          <section className="next-instructions">
            <div className="step-contents">
              <h3>You can now log into your account</h3>
              <p>
                It&apos;s fast and easy to manage your account online. You can
                set up recurring contributions, add another bank account, add or
                update beneficiaries, make a one-time contribution, and more.
              </p>
              <a
                // href="https://www.troweprice.com/usis/client-portal/dashboard"
                // TEMP SOLUTION FOR US378386
                // Send new/guest users to logout, then redirect to account access after logging in again
                // this updates the correct entitlements for users when attempting to view AAX dashboard
                href={
                  isGuestTypeUser
                    ? `${config('clxUrl')}/usis/login/logout?sdurl=${config(
                        'clxUrl'
                      )}/usis/client-portal/dashboard`
                    : `${config('clxUrl')}/usis/client-portal/dashboard`
                }
                target="_blank"
                data-name={generateGeneralTestID(
                  'Confirmation',
                  'GoToMyAccountButton'
                )}
                rel="noopener noreferrer">
                Go to my account
              </a>
            </div>
          </section>
          <RetirementContactSection />
        </>
      ) : (
        <section className="next-instructions">
          <div className="step-contents">
            <h3>You can now log into your account</h3>
            <p>
              It&apos;s fast and easy to manage your account online. You can set
              up recurring contributions, add another bank account, add or
              update beneficiaries, make a one-time contribution, and more.
            </p>
            {!rpsDirectRollover && (
              <p>
                The cost basis method for this account has been defaulted to
                Average Cost. The cost basis method can impact the amount of
                taxable gain or loss when shares are sold. If you wish to make
                changes to this selection, log into your account and go to the
                My Profile section to learn more.
              </p>
            )}
            <a
              // href="https://www.troweprice.com/usis/client-portal/dashboard"
              // TEMP SOLUTION FOR US378386
              // Send new/guest users to logout, then redirect to account access after logging in again
              // this updates the correct entitlements for users when attempting to view AAX dashboard
              href={
                isGuestTypeUser
                  ? `${config('clxUrl')}/usis/login/logout?sdurl=${config(
                      'clxUrl'
                    )}/usis/client-portal/dashboard`
                  : `${config('clxUrl')}/usis/client-portal/dashboard`
              }
              target="_blank"
              data-name={generateGeneralTestID(
                'Confirmation',
                'GoToMyAccountButton'
              )}
              rel="noopener noreferrer">
              Go to my account
            </a>
            <hr />A Trusted Contact is a resource for T. Rowe Price to contact
            on your behalf, if necessary, to help protect your accounts from
            financial exploitation, or to confirm your contact information if we
            are unable to reach you. Click{' '}
            <a
              href={
                isGuestTypeUser
                  ? `${config('clxUrl')}/usis/login/logout?sdurl=${config(
                      'clxUrl'
                    )}/usis/my-accounts/profile/trusted-contacts`
                  : `${config(
                      'clxUrl'
                    )}/usis/my-accounts/profile/trusted-contacts`
              }
              target="_blank"
              rel="noopener noreferrer">
              Trusted Contact
            </a>{' '}
            to add, or update and learn more.
          </div>
        </section>
      )}
      <style jsx>{styles}</style>
    </>
  );
};

const AppInfoSection = () => {
  return (
    <>
      <section className="next-instructions">
        <div className="step-contents">
          <h3 className="confirmation-section-headers">
            Download our mobile app
          </h3>
          <p>
            The T. Rowe Price app is available for IPhone, IPad and Android.
          </p>
          <img
            className="app-store-icon"
            src={appStoreIcon}
            alt="download app store"
          />
          <img src={playStoreIcon} alt="download play store" />
        </div>
      </section>
      <style jsx>{styles}</style>
    </>
  );
};

const ReferrerSection = ({ isRollover, loadedData, next }) => {
  return (
    <>
      <section className="next-instructions">
        <div className="step-contents">
          {loadedData?.submittedReferrer ? (
            <h3 className="confirmation-section-headers">Thank you!</h3>
          ) : (
            <>
              <h3 className="confirmation-section-headers">
                Did another customer refer you?
              </h3>
              <p>
                We&apos;d love to hear about it. Please provide their name and
                email.
              </p>

              {loadedData?.referrerError && (
                <p role="alert" aria-atomic="true" className="error-msg">
                  Name and email are required.
                </p>
              )}

              <div className="contact-info-wrapper">
                <InputField
                  type="text"
                  name="refFullName"
                  placeholder="Full Name"
                  required={!isRollover || loadedData?.referrerError}
                  className="blocked_data"
                />

                <InputField
                  type="email"
                  name="refEmail"
                  placeholder="Email Address"
                  required={!isRollover || loadedData?.referrerError}
                  validation={emailValidation}
                  className="blocked_data"
                />
              </div>
              <div className="button-container">
                <MFBeaconButton
                  name="submit"
                  className="continue-btn, beacon-button-margin-override"
                  variant="primary"
                  size="normal"
                  id="accountSubmitBtn"
                  onClick={next('referrer')}
                  label="Submit"
                  data-name="referrerSubmitBtn"
                />
              </div>
            </>
          )}
        </div>
      </section>
      <style jsx>{styles}</style>
    </>
  );
};

/* eslint-disable complexity */
const ConfirmationScreen = ({ appState, next }) => {
  const {
    store: { loadedData, accountType, chargeType, rpsDirectRollover }
  } = appState;

  // [US375779] prevent the browser back button from allowing the user
  // to go back to ReviewConfirm and submit again
  useEffect(() => {
    window.history.pushState(null, document.title, window.location.href);
    window.addEventListener('popstate', () =>
      window.history.pushState(null, document.title, window.location.href)
    );
  });

  // account type: Rollover
  const isRollover = accountType === accountTypes.ROLLOVER;

  // account type: Taxable Individual, Joint, or Ugma-Utma
  const isNonTransferOrTrustTaxable =
    chargeType !== chargeTypes.TRANSFER &&
    (accountType === accountTypes.INDIVIDUAL ||
      accountType === accountTypes.JOINT ||
      accountType === accountTypes.UGMAUTMA);

  return (
    <ExternalAuthenticator>
      <div>
        <div className="confirmation-wrapper">
          <section className="account-ready-message">
            <ConfirmationHeading />
          </section>

          {isTrustExistingFlow() || <h2 className="next-steps">Next Steps</h2>}

          {isNonFundedFlow(accountType) &&
          !isTaxableTransfer(accountType, chargeType) ? (
            // account types: Taxable (non-Trust) and Contributory
            <NonFundedConfirmation accountType={accountType} />
          ) : (
            // account types: Rollovers, Transfers, Taxable Transfers, and Trust
            <ConfirmationSection />
          )}

          {!isTrust(accountType) && !isNonFundedFlow(accountType) && (
            <LoginInfoSection
              accountType={accountType}
              rpsDirectRollover={rpsDirectRollover}
            />
          )}

          {!isTrust(accountType) && <AppInfoSection />}

          <ReferrerSection
            isRollover={isRollover}
            loadedData={loadedData}
            next={next}
          />
        </div>
        <PhoneNumber />
        {isRetirement(accountType) && (
          <div className="retirement-footnote">
            <p>
              *Pursuant to the terms of the T. Rowe Price Traditional and Roth
              IRA Disclosure Statement and Custodial Agreement.
            </p>
            <p>202502-4227076</p>
          </div>
        )}
        {isNonTransferOrTrustTaxable && (
          <div className="legal-reference-number">
            <p>202410-3874405</p>
          </div>
        )}
        <style jsx>{styles}</style>
      </div>
    </ExternalAuthenticator>
  );
};
/* eslint-enable complexity */

export default ConfirmationScreen;
